import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faTrophy } from '@fortawesome/pro-regular-svg-icons';
import { Text, useTheme } from '@querodelivery/uni-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styles from './mission-dashboard.module.scss';
import { MissionStatusBadge } from '../mission-status-badge/mission-status-badge';
import { EMissionType } from '../../constants';
import { MissionDataBox } from '../mission-data-box/mission-data-box';
import { MissionCitiesModal } from '../mission-cities-modal/mission-cities-modal';
import { MissionProgressBar } from '../mission-progress-bar/mission-progress-bar';
import { moneyLabel } from '@/Utils/FnUtils';
import { DeliveryAverageByDayBox } from '../delivery-average-by-day-box/delivery-average-by-day-box';
import { DeliverymanByMissionTable } from '../deliverymen-by-mission-table/deliverymen-by-mission-table';
import { useGetMissionById } from '../../api/get-mission-by-id';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';
import { MissionDashboardSkeleton } from './mission-dashboard-skeleton';
import { MissionChangeLog } from '../mission-change-log/mission-change-log';

const VISIBLE_CITIES_LIMIT = 3;

const formatNumber = (value?: number) => (value ?? 0).toLocaleString('pt-BR');

export const MissionDashboard = () => {
  const { missionId } = useParams<{ missionId: string }>();
  const navigation = useNavigate();
  const { color } = useTheme();
  const [isCitiesModalOpen, setIsCitiesModalOpen] = useState(false);

  const {
    data: mission,
    isLoading,
    isError,
  } = useGetMissionById(
    { missionId: missionId || '' },
    { enabled: !!missionId },
  );

  if (!missionId) {
    return <Navigate to="/missoes" replace />;
  }

  if (isLoading) {
    return <MissionDashboardSkeleton />;
  }

  if (isError || !mission) {
    return (
      <div className="container">
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigation('/missoes')}
        >
          <FontAwesomeIcon
            color={color['color-base-icon']}
            icon={faChevronLeft}
          />
          <Text variant="bodySm" tone="supporting">
            Voltar
          </Text>
        </button>
        <TableEmptyState
          title="Missão não encontrada"
          description="Não foi possível carregar os dados desta missão. Tente novamente mais tarde."
        />
      </div>
    );
  }

  const formatPeriod = (startDate: Date, endDate: Date) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };

    const formattedStart = start.toLocaleDateString('pt-BR', options);
    const formattedEnd = end.toLocaleDateString('pt-BR', options);

    return `Período: ${formattedStart} – ${formattedEnd}`;
  };

  const formatAwardDeliveriesLabel = (conditionValue: number) => {
    return conditionValue === 1
      ? `${conditionValue}+ entrega`
      : `${conditionValue}+ entregas`;
  };

  const { dashboard } = mission;
  const revenueColor =
    dashboard?.revenue && dashboard.revenue < 0 ? 'error' : 'success';

  const isCityMission = mission.type === EMissionType.CITY;
  const cities = mission.cities ?? [];
  const visibleCities = cities.slice(0, VISIBLE_CITIES_LIMIT);
  const hasHiddenCities = cities.length > VISIBLE_CITIES_LIMIT;
  const completionRateLabel =
    dashboard && dashboard.completionRate
      ? `${Math.round(dashboard.completionRate)}% dos participantes`
      : '0% dos participantes';

  const paymentDate = mission.payedAt
    ? new Date(mission.payedAt).toLocaleDateString('pt-BR')
    : dashboard?.paymentSchedule
    ? new Date(dashboard.paymentSchedule).toLocaleDateString('pt-BR')
    : '- - -';

  return (
    <div className="container">
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.leftHeader}>
            <button type="button" onClick={() => navigation(-1)}>
              <FontAwesomeIcon
                color={color['color-base-icon']}
                icon={faChevronLeft}
              />
            </button>
            <div className={styles.missionInfo}>
              <div className={styles.missionName}>
                <Text variant="heading2xl" tone="base">
                  {mission.name}
                </Text>
                <MissionStatusBadge status={mission.status} />
              </div>
              <Text variant="bodyXs" tone="supporting">
                {formatPeriod(mission.startDate, mission.endDate)}
              </Text>
              <Text variant="bodyXs" fontWeight="semibold" tone="supporting">
                Criada por: {mission.manager.name}
              </Text>
            </div>
          </div>
          <div />
          <div
            className={`${styles.rightHeader} ${
              isCityMission ? '' : styles.rightHeaderSingle
            }`}
          >
            <div className={styles.targetBox}>
              <Text
                variant="headingMd"
                tone="base"
                fontWeight="bold"
                className={styles.targetText}
              >
                {isCityMission ? 'Municípios: ' : 'Entregadores'}
                {isCityMission && visibleCities.length > 0 && (
                  <Text
                    as="span"
                    variant="bodySm"
                    tone="base"
                    fontWeight="medium"
                  >
                    {visibleCities.map(city => city.name).join(', ')}
                    {hasHiddenCities && '...'}
                  </Text>
                )}
              </Text>
              {isCityMission && hasHiddenCities && (
                <button
                  type="button"
                  className={styles.seeMoreButton}
                  onClick={() => setIsCitiesModalOpen(true)}
                >
                  <Text
                    variant="bodyXs"
                    tone="highlight"
                    fontWeight="medium"
                    textDecorationLine="underline"
                  >
                    Ver mais
                  </Text>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.dataBoxes}>
          <MissionDataBox
            label="Total de entregas"
            value={formatNumber(dashboard?.totalDeliveries)}
          />
          <MissionDataBox
            label="Total de participantes"
            value={formatNumber(dashboard?.totalDeliverymen)}
            supportLabel="entregadores ativos"
          />
          <MissionDataBox
            label="Atingiram a meta"
            value={formatNumber(dashboard?.reached)}
            supportLabel={completionRateLabel}
          />
          <MissionDataBox
            label="Média de entregas"
            value={formatNumber(dashboard?.deliveryAverageByDeliveryman)}
            supportLabel="por participante"
          />
          <MissionDataBox
            label="Média de dias"
            value={formatNumber(dashboard?.daysAverage)}
            supportLabel="para chegar ao objetivo"
          />
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.mainBox}>
            <DeliverymanByMissionTable missionId={missionId} />
          </div>
          <div className={styles.topRightBox}>
            <div className={styles.awardsBox}>
              <div className={styles.awardHeader}>
                <FontAwesomeIcon
                  icon={faTrophy}
                  color={color['color-text-highlight']}
                />
                <Text variant="headingSm" tone="base" fontWeight="bold">
                  Premiações
                </Text>
              </div>
              <div className={styles.awardList}>
                {mission.rewardLevels.map((reward, index) => (
                  <div key={index} className={styles.awardItem}>
                    <Text
                      variant="bodyXs"
                      tone="supporting"
                      fontWeight="medium"
                      lineHeight="sm"
                    >
                      {formatAwardDeliveriesLabel(reward.conditionValue)}
                    </Text>
                    <Text
                      variant="headingLg"
                      tone="highlight"
                      fontWeight="bold"
                      lineHeight="lg"
                    >
                      {moneyLabel(reward.rewardValue)}
                    </Text>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.topRightItems}>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  Taxa de conclusão
                </Text>
                {dashboard && (
                  <MissionProgressBar
                    value={dashboard.completionRate}
                    accessibilityLabel="Taxa de conclusão"
                    className={styles.completionRateBar}
                  />
                )}
              </div>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  Data de criação
                </Text>
                <Text variant="headingLg" tone="supporting" fontWeight="bold">
                  {new Date(mission.createdAt).toLocaleDateString('pt-BR')}
                </Text>
              </div>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  {mission.payedAt
                    ? 'Data de pagamento'
                    : 'Previsão de pagamento'}
                </Text>
                <Text variant="headingLg" tone="supporting" fontWeight="bold">
                  {paymentDate}
                </Text>
              </div>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  Orçamento
                </Text>
                <Text variant="headingLg" tone="supporting" fontWeight="bold">
                  {moneyLabel(mission.budget)}
                </Text>
              </div>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  Total investido
                </Text>
                <Text variant="headingLg" tone="supporting" fontWeight="bold">
                  {moneyLabel(dashboard?.totalInvested ?? 0)}
                </Text>
              </div>
              <div className={styles.topRightItem}>
                <Text
                  variant="bodyXs"
                  tone="base"
                  fontWeight="medium"
                  lineHeight="lg"
                >
                  Receita
                </Text>
                <Text variant="headingLg" tone={revenueColor} fontWeight="bold">
                  {moneyLabel(dashboard?.revenue ?? 0)}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.bottomRightBox}>
            <DeliveryAverageByDayBox
              total={dashboard?.deliveryAverage?.averageByDay ?? 0}
              values={dashboard?.deliveryAverage?.days ?? []}
            />
          </div>
        </div>
      </div>
      {!!mission.changeLog?.length && (
        <MissionChangeLog changeLog={mission.changeLog} />
      )}

      <MissionCitiesModal
        isOpen={isCitiesModalOpen}
        cities={cities}
        onClose={() => setIsCitiesModalOpen(false)}
      />
    </div>
  );
};
